import { useLocalSearchParams } from "expo-router"
import { View, ActivityIndicator } from "react-native"
import { SafeAreaView } from "react-native-safe-area-context"
import MCIcon from "@react-native-vector-icons/material-design-icons"

import { ar } from "i18n/ar"
import { List, Text, Header } from "@src/components"
import { formatPrice, formatDateForDisplay, sharedStyles, trpc } from "@src/utils"

export default function SupplierBalanceTrackerScreen() {
  const { id } = useLocalSearchParams<{ id: string }>()
  const supplierId = Number.parseInt(id as string, 10)

  const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isLoading, refetch } = trpc.suppliers.balanceHistory.useInfiniteQuery(
    { supplierId, limit: 25 },
    { getNextPageParam: (lastPage) => lastPage.nextCursor },
  )

  const entries = data?.pages.flatMap((page) => page.items) ?? []

  return (
    <View className="flex-1 bg-gray-100">
      <Header title={ar.balanceHistory} />

      <List
        className="flex-1"
        contentContainerStyle={sharedStyles.contentContainer}
        data={entries}
        keyExtractor={(item) => item.id.toString()}
        onEndReached={() => {
          if (hasNextPage && !isFetchingNextPage) fetchNextPage()
        }}
        onEndReachedThreshold={0.3}
        onRefresh={refetch}
        ListEmptyComponent={
          isLoading ? (
            <View className="bg-white p-12 rounded-xl shadow-sm items-center">
              <ActivityIndicator size="large" color="#10B981" />
              <Text className="text-gray-500 mt-4 font-medium">{ar.loading}</Text>
            </View>
          ) : (
            <View className="bg-white p-12 rounded-xl shadow-sm items-center">
              <View className="w-16 h-16 bg-gray-50 rounded-full items-center justify-center mb-4">
                <MCIcon name="history" size={32} color="#9CA3AF" />
              </View>
              <Text className="text-gray-900 font-bold text-lg mb-1">{ar.noTransactions}</Text>
            </View>
          )
        }
        renderItem={({ item }) => {
          const isPositive = item.amount > 0
          return (
            <View className="rounded-lg border border-gray-200 bg-white shadow-sm mb-3 p-4">
              <View className="flex-row justify-between items-center mb-3">
                <View className="flex-row items-center flex-1 gap-3">
                  <View className={`w-10 h-10 rounded-full items-center justify-center ${isPositive ? "bg-green-50" : "bg-red-50"}`}>
                    <MCIcon name={isPositive ? "arrow-up" : "arrow-down"} size={20} color={isPositive ? "#10B981" : "#EF4444"} />
                  </View>
                  <View className="flex-1">
                    <Text className="text-sm text-gray-500">{formatDateForDisplay(item.date, "D MMM YYYY • hh:mm a")}</Text>
                    <Text className="text-xs text-gray-400 mt-0.5">
                      {ar.recordedAt}: {formatDateForDisplay(item.createdAt, "D MMM YYYY • hh:mm a")}
                    </Text>
                  </View>
                </View>

                <View className={`px-3 py-1.5 rounded-lg ${isPositive ? "bg-green-50" : "bg-red-50"}`}>
                  <Text className={`font-bold text-base ${isPositive ? "text-green-700" : "text-red-700"}`}>
                    {isPositive ? "+" : ""}
                    {formatPrice(item.amount)} {ar.egp}
                  </Text>
                </View>
              </View>

              <View className="flex-row gap-3">
                <View className="flex-1 bg-gray-50 rounded-lg p-2.5">
                  <Text className="text-xs text-gray-500 mb-0.5">{ar.balanceBefore}</Text>
                  <Text className="font-semibold text-gray-700">
                    {formatPrice(item.balanceBefore)} {ar.egp}
                  </Text>
                </View>
                <View className="flex-1 bg-gray-50 rounded-lg p-2.5">
                  <Text className="text-xs text-gray-500 mb-0.5">{ar.balanceAfter}</Text>
                  <Text className="font-semibold text-gray-900">
                    {formatPrice(item.balanceAfter)} {ar.egp}
                  </Text>
                </View>
              </View>

              {item.note ? (
                <View className="mt-3 pt-3 border-t border-gray-100">
                  <Text className="text-sm text-gray-600 leading-5">{item.note}</Text>
                </View>
              ) : null}
            </View>
          )
        }}
        ListFooterComponent={
          <>
            {isFetchingNextPage && (
              <View className="py-4 items-center">
                <ActivityIndicator size="small" color="#10B981" />
              </View>
            )}
            <SafeAreaView edges={["bottom"]} />
          </>
        }
      />
    </View>
  )
}
